import {
  Button,
  Divider,
  Group,
  Paper,
  Stack,
  Tabs,
  Text,
  Title,
} from "@mantine/core";
import {
  IconCalendar,
  IconCalendarPlus,
  IconClipboardText,
  IconFileDescription,
  IconMessage,
} from "@tabler/icons-react";
import { useState } from "react";
import { client } from "@/api-client";
import { FancyLoader } from "@/components/FancyLoader";
import { Header } from "@/components/Header";
import { LibraryItem } from "@/components/LibraryItem";
import { BaseSessionCard } from "@/components/SessionCard";
import { SurveyList } from "@/components/SurveyList";
import { OwnerRoutes, UniversalRoutes, useNavigator } from "@/routers/routes";
import {
  FutureSessionStatuses,
  groupBySessions,
  PastSessionStatuses,
} from "@/utils/sessions";
import type { SurveyStatus } from "@/utils/survey";

export const TrainerEngagement = (props: {
  params: {
    engagementId: string;
  };
}) => {
  const { navigator } = useNavigator();
  const { engagementId } = props.params;
  const [activeTab, setActiveTab] = useState<string | null>("profile");
  const [_selectedSurveyId, setSelectedSurveyId] = useState<string | null>(
    null,
  );

  // Fetch trainer data from engagement
  const { data: engagement, isLoading: isLoadingEngagement } = client.useQuery(
    "get",
    "/owner/engagements/{engagementId}",
    {
      params: {
        path: {
          engagementId,
        },
      },
    },
  );

  const { data: trainer, isLoading: isLoadingTrainer } = client.useQuery(
    "get",
    "/owner/trainers/{trainerId}",
    {
      params: { path: { trainerId: engagement?.trainer.id ?? "" } },
    },
    { enabled: !!engagement },
  );

  // Get survey statuses for this engagement
  const { data: engagementData, isLoading: isLoadingSurveyStatuses } =
    client.useQuery("get", "/owner/engagements/{engagementId}", {
      params: {
        path: {
          engagementId,
        },
      },
    });

  if (isLoadingEngagement || isLoadingTrainer || !engagement || !trainer) {
    return <FancyLoader />;
  }

  const files = engagement?.files;
  const sessions = engagement?.sessions;

  const { futureSessions, pastSessions } = groupBySessions(sessions, {
    futureSessions: [...FutureSessionStatuses],
    pastSessions: [...PastSessionStatuses],
  });

  const navigateToChat = navigator({
    path: UniversalRoutes.CHAT.DETAIL,
    params: { engagementId },
  });

  const navigateToBookSession = () => {
    if (trainer?.id) {
      navigator({
        path: OwnerRoutes.REQUEST_SESSION,
        params: { trainerId: trainer.id },
      })();
    }
  };

  const handleSurveyClick = (fileId: string, _status: SurveyStatus) => {
    setSelectedSurveyId(fileId);
    navigator({
      path: OwnerRoutes.SURVEYS.FILL,
      params: {
        engagementId,
        surveyId: fileId,
      },
    })();
  };

  return (
    <Stack gap="md">
      <Header title={`${trainer.firstName} ${trainer.lastName}`} />

      <Paper p="md" radius="sm" withBorder>
        <Group gap="xl" align="start">
          <Stack gap="xs" style={{ flex: 1 }}>
            <Title order={3}>
              {trainer.firstName} {trainer.lastName}
            </Title>
            <Text size="sm" c="dimmed">
              {trainer.locality}
            </Text>
          </Stack>
        </Group>
        <Group grow mt="md">
          <Button
            leftSection={<IconCalendarPlus size={18} />}
            onClick={navigateToBookSession}
          >
            Book Again
          </Button>
          <Button
            leftSection={<IconMessage size={18} />}
            onClick={navigateToChat}
            color="second.9"
          >
            Chat
          </Button>
        </Group>
      </Paper>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List grow>
          <Tabs.Tab
            value="profile"
            leftSection={<IconCalendar size={"1rem"} />}
          >
            Sessions
          </Tabs.Tab>
          <Tabs.Tab
            value="files"
            leftSection={<IconFileDescription size={"1rem"} />}
          >
            Shared Files
          </Tabs.Tab>
          <Tabs.Tab
            value="surveys"
            leftSection={<IconClipboardText size={"1rem"} />}
          >
            Surveys
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="profile" pt="md">
          <Stack gap="md">
            {futureSessions.length > 0 && (
              <>
                <Title order={4}>Upcoming Sessions</Title>
                {futureSessions.map((session) => (
                  <BaseSessionCard
                    key={session.id}
                    session={session}
                    who="owner"
                  />
                ))}
              </>
            )}

            {pastSessions.length > 0 && (
              <>
                <Divider my="md" />
                <Title order={4}>Past Sessions</Title>
                {pastSessions.map((session) => (
                  <BaseSessionCard
                    key={session.id}
                    session={session}
                    who="owner"
                  />
                ))}
              </>
            )}

            {futureSessions.length === 0 && pastSessions.length === 0 && (
              <Text c="dimmed">No sessions with this trainer yet.</Text>
            )}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="files" pt="md">
          <Stack gap="md">
            {files.length > 0 ? (
              <>
                <Title order={4}>Shared Files</Title>
                {files.map((file) => (
                  <LibraryItem
                    key={file.id}
                    kind="file"
                    item={file}
                    readonly={true}
                    onFileClick={navigator({
                      path: UniversalRoutes.FILE_PREVIEW,
                      params: { fileId: file.id },
                    })}
                  />
                ))}
              </>
            ) : (
              <Text c="dimmed">No files have been shared yet.</Text>
            )}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="surveys" pt="md">
          <SurveyList
            files={files.filter((file) => file.type === "SURVEY")}
            surveyStatuses={engagementData?.surveys || {}}
            onSurveyClick={handleSurveyClick}
            isLoading={isLoadingSurveyStatuses}
          />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
};
